// ─── What: brush, wheel zoom, drag pan, edge glow ─────────────────────────────
//
// All of it hangs off the brush's own overlay rect, which is the only element
// covering the whole plot. The crosshair binds to the same rect afterwards.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import { zoomDomain, panDomain, sameDomain } from '../../chartZoom';
import { MARGIN } from '../layout';
import type { ChartGroup, DrawCtx } from '../drawContext';
import type { ChartViewport } from './useChartViewport';

const EDGE_GLOW_W = 28;

export interface ViewportDeps extends ChartViewport {
    /** Zoom/pan limits — the full event range, not just the loaded window. */
    bounds: [Date, Date];
    timeRange: [Date, Date] | null;
    onTimeRangeChange: (range: [Date, Date] | null) => void;
    svgRef: React.RefObject<SVGSVGElement>;
}

/** Returns the brush group so the crosshair can bind to the same overlay rect. */
export function attachViewport(ctx: DrawCtx, deps: ViewportDeps): ChartGroup {
    const { svg, defs, layout, theme, xScale, fullDomain } = ctx;
    const {
        bounds,
        timeRange,
        liveDomain,
        setLiveDomain,
        commitRange,
        onTimeRangeChange,
        dragCleanupRef,
        edgeHitRef,
        svgRef,
    } = deps;
    const w = layout.w;

    // ── Brush (marquee zoom-select) ───────────────────────────────────────
    // Shift-drag only; a plain drag pans instead.
    const brush = d3
        .brushX()
        .filter(event => event.shiftKey && !event.button)
        .extent([
            [MARGIN.left, MARGIN.top],
            [MARGIN.left + w, layout.probHeight],
        ])
        .on('end', event => {
            if (!event.sourceEvent || !event.selection) return;
            const [x0, x1] = event.selection as [number, number];
            brushGroup.call(brush.move, null);
            onTimeRangeChange([xScale.invert(x0), xScale.invert(x1)]);
        });

    const brushGroup = svg
        .append('g')
        .attr('class', 'brush')
        .call(brush) as ChartGroup;

    const atFull = (r: [Date, Date]) =>
        r[0].getTime() <= bounds[0].getTime() &&
        r[1].getTime() >= bounds[1].getTime();

    const overlay = brushGroup.select<SVGRectElement>('.overlay');

    // Re-selects from the live root every call: each setLiveDomain rebuilds the
    // SVG, so a captured selection would be detached after one move.
    const showEdgeGlow = (edge: 'start' | 'end' | null) => {
        edgeHitRef.current = edge;
        const root = d3.select(svgRef.current);
        root.select('.edge-glow-start').attr('opacity', edge === 'start' ? 1 : 0);
        root.select('.edge-glow-end').attr('opacity', edge === 'end' ? 1 : 0);
    };

    // ── Wheel zoom ────────────────────────────────────────────────────────
    overlay.on('wheel.zoom', (event: WheelEvent) => {
        event.preventDefault();
        const [mx] = d3.pointer(event, overlay.node());
        const cursorTime = xScale.invert(mx);
        const cur = (liveDomain ?? timeRange ?? fullDomain) as [Date, Date];
        const factor = event.deltaY > 0 ? 1.2 : 0.8;
        const next = zoomDomain(cur, cursorTime, factor, bounds);
        // Already at the limit — don't re-render or refetch for a no-op.
        const wheelMsPerPx = (cur[1].getTime() - cur[0].getTime()) / w;
        if (sameDomain(next, cur, wheelMsPerPx)) return;
        setLiveDomain(next);
        commitRange(atFull(next) ? null : next);
    });

    overlay.on('dblclick.reset', () => {
        setLiveDomain(null);
        commitRange(null);
    });

    // ── Drag pan ──────────────────────────────────────────────────────────
    // Listens on window so the gesture survives the SVG rebuild that each
    // setLiveDomain triggers, and computes from the drag-start domain so there
    // is no drift across those rebuilds.
    overlay.on('mousedown.pan', (event: MouseEvent) => {
        if (event.shiftKey || event.button !== 0) return; // shift = brush
        event.preventDefault(); // suppress text/label selection while panning
        const startX = event.clientX;
        const startDomain = (liveDomain ?? timeRange ?? fullDomain) as [
            Date,
            Date,
        ];
        const msPerPx =
            (startDomain[1].getTime() - startDomain[0].getTime()) / w;
        let lastDomain = startDomain;
        let moved = false;

        const onMove = (e: MouseEvent) => {
            moved = true;
            const deltaMs = -(e.clientX - startX) * msPerPx;
            const next = panDomain(startDomain, deltaMs, bounds);
            // panDomain clamps silently — compare against the unclamped shift to
            // learn which bound, if any, swallowed the drag.
            const wantLeft = startDomain[0].getTime() + deltaMs;
            const wantRight = startDomain[1].getTime() + deltaMs;
            // Direct DOM write, so the glow tracks the cursor even while the
            // guard below is suppressing renders.
            showEdgeGlow(
                next[0].getTime() > wantLeft + 1
                    ? 'start'
                    : next[1].getTime() < wantRight - 1
                      ? 'end'
                      : null
            );
            // Dragging further into a bound yields the same window every move.
            // Re-rendering it would redraw the chart and, on commit, refetch
            // trades and traders for a window that never changed.
            if (sameDomain(next, lastDomain, msPerPx / 2)) return;
            lastDomain = next;
            setLiveDomain(next);
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            dragCleanupRef.current = null;
            showEdgeGlow(null);
            // Only commit (refetch) if the window actually moved — a plain click
            // must not re-commit the same range.
            if (moved && !sameDomain(lastDomain, startDomain, msPerPx)) {
                commitRange(atFull(lastDomain) ? null : lastDomain);
            }
        };

        dragCleanupRef.current = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            edgeHitRef.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    });

    // ── Pan edge indicator ────────────────────────────────────────────────
    // Lights up the side a drag is pushing against, so hitting "today" or the
    // oldest candle reads as a limit rather than a frozen chart.
    const glowIds: Record<'start' | 'end', string> = {
        start: 'edge-glow-s-' + Math.random().toString(36).slice(2, 8),
        end: 'edge-glow-e-' + Math.random().toString(36).slice(2, 8),
    };
    for (const side of ['start', 'end'] as const) {
        const grad = defs
            .append('linearGradient')
            .attr('id', glowIds[side])
            .attr('x1', side === 'start' ? '0%' : '100%')
            .attr('x2', side === 'start' ? '100%' : '0%')
            .attr('y1', '0%')
            .attr('y2', '0%');
        grad.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', theme.palette.primary.main)
            .attr('stop-opacity', 0.18);
        grad.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', theme.palette.primary.main)
            .attr('stop-opacity', 0);
    }
    for (const side of ['start', 'end'] as const) {
        svg.append('rect')
            .attr('class', `edge-glow-${side}`)
            .attr(
                'x',
                side === 'start' ? MARGIN.left : MARGIN.left + w - EDGE_GLOW_W
            )
            .attr('y', MARGIN.top)
            .attr('width', EDGE_GLOW_W)
            .attr('height', Math.max(0, layout.probHeight - MARGIN.top))
            .attr('fill', `url(#${glowIds[side]})`)
            // Survives a mid-drag rebuild: the ref remembers which bound is hit.
            .attr('opacity', edgeHitRef.current === side ? 1 : 0)
            .style('pointer-events', 'none');
    }

    brushGroup
        .selectAll('.selection')
        .attr('fill', alpha(theme.palette.primary.main, 0.12))
        .attr('stroke', alpha(theme.palette.primary.main, 0.4))
        .attr('stroke-width', 1);

    return brushGroup;
}
